import { forwardRef, useCallback, useId, useImperativeHandle, useRef, useState } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { ChevronDown } from "lucide-react";
import type { Language } from "@/domain";
import { cn } from "@/utils";

const LANGUAGE_OPTIONS: Array<{ code: Language; label: string }> = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "ca", label: "Català" },
];

interface LanguageDropdownProps extends Omit<React.ComponentPropsWithRef<"div">, "onChange"> {
  value: Language;
  onChange: (languageCode: Language) => void;
  placeholder?: string;
}

export const LanguageDropdown = forwardRef<HTMLDivElement, LanguageDropdownProps>(
  ({ value, onChange, placeholder = "Seleccionar idioma", className, ...props }, ref) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => dropdownRef.current!);
    const listboxId = useId();

    const closeDropdown = useCallback(() => setShowDropdown(false), []);
    useClickOutside(dropdownRef, closeDropdown, showDropdown);

    const handleSelect = useCallback(
      (index: number) => {
        const option = LANGUAGE_OPTIONS[index];
        if (!option) return;
        onChange(option.code);
        setShowDropdown(false);
      },
      [onChange],
    );

    const { highlightedIndex, getKeyboardProps } = useKeyboardNavigation({
      itemCount: LANGUAGE_OPTIONS.length,
      isOpen: showDropdown,
      onSelect: handleSelect,
      onClose: closeDropdown,
    });

    const selectedLanguage = LANGUAGE_OPTIONS.find((lang) => lang.code === value);
    const activeDescendant =
      highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined;

    return (
      <div className={cn("relative", className)} ref={dropdownRef} {...props}>
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          {...getKeyboardProps()}
          className={cn(
            "w-full px-2 py-1 text-xs border border-border-secondary rounded-md text-left hover:bg-bg-secondary focus:outline-none focus:ring-1 focus:ring-ring-focus focus:border-transparent flex items-center justify-between transition-colors",
            value ? "bg-bg-secondary" : "bg-bg-primary",
          )}
        >
          <span className={value ? "text-text-primary" : "text-text-tertiary"}>
            {selectedLanguage?.label || placeholder}
          </span>
          <ChevronDown
            size={14}
            className={cn("text-text-tertiary transition-transform", showDropdown && "rotate-180")}
          />
        </button>

        {showDropdown && (
          <div
            id={listboxId}
            role="listbox"
            className="absolute z-10 w-full mt-1 bg-bg-primary border border-border-secondary rounded-md shadow-lg max-h-48 overflow-y-auto"
          >
            <div className="py-1">
              {LANGUAGE_OPTIONS.map((language, index) => (
                <button
                  key={language.code}
                  id={`${listboxId}-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={language.code === value}
                  className={cn(
                    "w-full px-3 py-2 text-xs text-left hover:bg-bg-secondary focus:bg-bg-secondary focus:outline-none flex items-center justify-between",
                    highlightedIndex === index && "bg-bg-tertiary",
                  )}
                  onClick={() => {
                    onChange(language.code);
                    setShowDropdown(false);
                  }}
                >
                  <span>{language.label}</span>
                  <span className="text-2xs text-text-tertiary">{language.code.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
);

LanguageDropdown.displayName = "LanguageDropdown";
